import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/public/Home";
import Services from "@/pages/public/Services";
import ServiceDetail from "@/pages/public/ServiceDetail";
import NexusAI from "@/pages/public/NexusAI";
import Pricing from "@/pages/public/Pricing";
import RequestService from "@/pages/public/RequestService";
import Contact from "@/pages/public/Contact";
import Book from "@/pages/public/Book";
import About from "@/pages/public/About";
import Portfolio from "@/pages/public/Portfolio";
import { Blog, BlogPost } from "@/pages/public/Blog";
import Chat from "@/pages/public/Chat";
import { Legal } from "@/pages/public/Misc";
import DigitalDownloads from "@/pages/public/DigitalDownloads";
import ProductDetail from "@/pages/public/ProductDetail";
import PayInvoicePage from "@/pages/public/PayInvoice";
import PaymentSuccess from "@/pages/public/PaymentSuccess";

import { OwnerLogin, CustomerLogin, CustomerRegister } from "@/pages/auth/Login";
import { ForgotPassword, ResetPassword } from "@/pages/auth/PasswordReset";
import AcceptInvite from "@/pages/auth/AcceptInvite";
import PlatformAdmin from "@/pages/platform/PlatformAdmin";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Leads from "@/pages/dashboard/Leads";
import Clients from "@/pages/dashboard/Clients";
import Contracts from "@/pages/dashboard/Contracts";
import Requests from "@/pages/dashboard/Requests";
import Appointments from "@/pages/dashboard/Appointments";
import DashServices from "@/pages/dashboard/Services";
import DashPricing from "@/pages/dashboard/Pricing";
import Estimates from "@/pages/dashboard/Estimates";
import Invoices from "@/pages/dashboard/Invoices";
import Recurring from "@/pages/dashboard/Recurring";
import Statements from "@/pages/dashboard/Statements";
import DigitalProducts from "@/pages/dashboard/DigitalProducts";
import RepairJobs from "@/pages/dashboard/RepairJobs";
import Projects from "@/pages/dashboard/Projects";
import Reports from "@/pages/dashboard/Reports";
import KnowledgeBase from "@/pages/dashboard/KnowledgeBase";
import AITest from "@/pages/dashboard/AITest";
import Content from "@/pages/dashboard/Content";
import BlogAdmin from "@/pages/dashboard/BlogAdmin";
import PortfolioAdmin from "@/pages/dashboard/PortfolioAdmin";
import EmailTemplates from "@/pages/dashboard/EmailTemplates";
import Subscription from "@/pages/dashboard/Subscription";
import Broadcasts from "@/pages/dashboard/Broadcasts";
import Users from "@/pages/dashboard/Users";
import AuditLogs from "@/pages/dashboard/AuditLogs";

import Portal from "@/pages/portal/Portal";

const STAFF = ["owner", "admin", "technician", "sales", "content_manager"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/nexus-ai" element={<NexusAI />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/request-service" element={<RequestService />} />
            <Route path="/digital-downloads" element={<DigitalDownloads />} />
            <Route path="/digital-downloads/:slug" element={<ProductDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/book" element={<Book />} />
            <Route path="/pay-invoice" element={<PayInvoicePage />} />
            <Route path="/legal/privacy" element={<Legal kind="privacy" />} />
            <Route path="/legal/terms" element={<Legal kind="terms" />} />
          </Route>

          <Route path="/chat" element={<Chat />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PayInvoicePage />} />

          <Route path="/owner/login" element={<OwnerLogin />} />
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          <Route path="/dashboard" element={<ProtectedRoute roles={STAFF}><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<Overview />} />
            <Route path="leads" element={<Leads />} />
            <Route path="clients" element={<Clients />} />
            <Route path="contracts" element={<Contracts />} />
            <Route path="requests" element={<Requests />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="services" element={<DashServices />} />
            <Route path="pricing" element={<DashPricing />} />
            <Route path="estimates" element={<Estimates />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="recurring" element={<Recurring />} />
            <Route path="statements" element={<Statements />} />
            <Route path="downloads" element={<DigitalProducts />} />
            <Route path="repair-jobs" element={<RepairJobs />} />
            <Route path="projects" element={<Projects />} />
            <Route path="reports" element={<Reports />} />
            <Route path="knowledge" element={<KnowledgeBase />} />
            <Route path="ai" element={<AITest />} />
            <Route path="content" element={<Content />} />
            <Route path="blog" element={<BlogAdmin />} />
            <Route path="portfolio" element={<PortfolioAdmin />} />
            <Route path="email-templates" element={<EmailTemplates />} />
            <Route path="broadcasts" element={<Broadcasts />} />
            <Route path="users" element={<Users />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="audit" element={<AuditLogs />} />
          </Route>

          <Route path="/portal" element={<ProtectedRoute roles={["customer"]}><Portal /></ProtectedRoute>} />
          <Route path="/platform" element={<ProtectedRoute roles={["super_admin"]}><PlatformAdmin /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
